import {useCallback, useEffect, useState} from 'react'
import {Modal, Spinner} from 'react-bootstrap'
import {KTSVG} from '../../_metronic/helpers'
import ContactListFilter from './ContactListFilter'
import {useContact} from '../hooks/useContact'
import {useGetAllTagsQuery} from '../../services/cellcast'
import CursorPaginate from './PrevNextPaginate'
import {ContactGroupMap, ContactStatus} from '../../services/cellcast.v2.types'
import moment from 'moment'
import SelectFloatingButton from './SelectFloatingButton'

const QMContactListPopup = ({
  show,
  handleClose,
  status,
  selectedCallback,
  returnFullContactData = false,
}: any) => {
  // Api Fetching Hook
  const {loading, data, actions} = useContact('QMContactListPopup', false, {
    status: ContactStatus.subscribe,
  })
  const [groupField, setGroupField] = useState<string[]>([])

  useEffect(() => {
    if (data.group && data.group.data && data.group.data.fields) {
      setGroupField(data.group.data.fields)
    }
  }, [data.group])

  useEffect(() => {
    if (show) {
      actions.setTriggerFetch()
    }
  }, [show])

  // load all tag
  const {isLoading: tagLoad, data: tagsData} = useGetAllTagsQuery(null)

  // local state
  const [selectedContactGroupIDs, setSelectedContactGroupIDs] = useState<string[]>([])

  // helper
  const getMaxTagsSize = useCallback((contacts: ContactGroupMap[]): number => {
    let tagsCharLength = 0
    contacts.forEach((el) => {
      if (el.tags?.length > 0) {
        let tagCharSize = 0
        el.tags.forEach((el) => {
          tagCharSize += el.name?.length
        })

        tagsCharLength += tagCharSize
      }
    })

    return tagsCharLength
  }, [])

  // actions
  const handleDoFetchData = useCallback(() => {
    actions.setParams({...data.params, page: ''})
    actions.setTriggerFetch()
  }, [actions, data.params])

  const handleCheckAll = () => {
    if (selectedContactGroupIDs?.length === data.contacts?.length) {
      setSelectedContactGroupIDs([])
    } else {
      let list = data.contacts.map((key: ContactGroupMap) => key._id)
      setSelectedContactGroupIDs([...list])
    }
  }

  const handleCheck = (e: ContactGroupMap) => {
    if (selectedContactGroupIDs.includes(e._id)) {
      // update selected contact group ids
      selectedContactGroupIDs.splice(selectedContactGroupIDs.indexOf(e._id), 1)
      setSelectedContactGroupIDs([...selectedContactGroupIDs])
    } else {
      setSelectedContactGroupIDs([...selectedContactGroupIDs, e._id])
    }
  }

  const handleChangSizeData = (size: number) => {
    actions.setParams({...data.params, limit: size, page: ''})
    actions.setTriggerFetch()
  }

  const handleChangePage = useCallback(
    (page: number | null, next: boolean) => {
      if (page === null || loading.contactFetch || loading.contactLoad) {
        return
      }

      if (next) {
        actions.setParams({...data.params, page: page})
        actions.setTriggerFetchNext()
      } else {
        actions.setParams({...data.params, page: page})
        actions.setTriggerFetchPrev()
      }

      setSelectedContactGroupIDs([])
    },
    [actions, data.params]
  )

  const handleSelectCallback = () => {
    if (selectedContactGroupIDs?.length > 0 && selectedCallback) {
      const foundContacts = data.contacts.filter((el: any) =>
        selectedContactGroupIDs.includes(el._id)
      )
      if (foundContacts && foundContacts?.length > 0) {
        if (returnFullContactData) {
          const contactsData = foundContacts.map((el: any) => ({
            id: el._id || el.id,
            number: el.number,
            fields: el.fields || [],
            tags: el.tags || [],
            createdAt: el.createdAt,
          }))
          selectedCallback(contactsData)
        } else {
          const foundContactNumbers = foundContacts.map((el: any) => el.number)
          selectedCallback(foundContactNumbers)
        }
        setSelectedContactGroupIDs([])
      }
    }
  }

  const checkField = useCallback(() => {
    let fields = ['First Name', 'Full Name', 'Last Name']
    if (groupField?.find((val: any) => fields.includes(val)) === undefined) {
      return false
    } else {
      return true
    }
  }, [groupField])

  return (
    <Modal
      tabIndex={-1}
      aria-hidden='true'
      size='lg'
      aria-labelledby='contained-modal-title-vcenter'
      // centered
      show={show}
      scrollable={false}
      className='fade w-80'
    >
      <div className='modal-header'>
        <h2>Contacts List</h2>
        <div className='btn btn-icon btn-sm' onClick={handleClose}>
          <KTSVG className='svg-icon-1' path='/media/icons/duotune/arrows/arr061.svg' />
        </div>
      </div>
      <div
        className='modal-body'
        style={{
          paddingTop: 0,
          paddingBottom: 0,
        }}
      >
        <div className='card'>
          <div className='card-header align-items-center border-bottom-0'>
            <ContactListFilter
              params={data.params}
              setParams={actions.setParams}
              doFetchData={handleDoFetchData}
              tags={tagsData?.data ? tagsData.data : []}
              showFilterStatus={false}
            />
            {selectedContactGroupIDs?.length > 0 && (
              <button
                className='btn msg-primary-btn send btn-primary'
                onClick={() => handleSelectCallback()}
              >
                {`select ${selectedContactGroupIDs?.length} data`}
              </button>
            )}
          </div>
        </div>
        <div className='card-body'>
          <div
            style={{
              maxHeight: 400,
              maxWidth: '100%',
              overflow: 'scroll',
              minHeight:
                data.contacts?.length < 10 || loading.contactFetch || loading.contactLoad
                  ? 450
                  : 300,
            }}
          >
            {loading.contactLoad ? (
              <div className='row flex-center h-200' style={{minHeight: 200}}>
                <Spinner animation='grow' />
              </div>
            ) : (
              <div className='table-responsive'>
                <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                  <thead>
                    <tr className='fw-bold text-muted text-uppercase'>
                      <th className='w-25px'>
                        <div className='form-check form-check-sm form-check-custom form-check-solid'>
                          <input
                            className='form-check-input'
                            type='checkbox'
                            value='ALL'
                            data-kt-check='true'
                            data-kt-check-target='.widget-9-check'
                            checked={
                              selectedContactGroupIDs?.length >= data.contacts?.length &&
                              data.contacts?.length > 0
                            }
                            onChange={handleCheckAll}
                          />
                        </div>
                      </th>
                      <th>Mobile</th>
                      <th style={{minWidth: getMaxTagsSize(data.contacts) > 10 ? 250 : 0}}>Tags</th>
                      {checkField() && groupField.includes('First Name') && <th>First Name</th>}
                      {checkField() && groupField.includes('Last Name') && <th>Last Name</th>}
                      {checkField() && groupField.includes('Full Name') && <th>Full Name</th>}

                      {groupField.map((val, i) => {
                        if (!['First Name', 'Full Name', 'Last Name']?.includes(val)) {
                          return <th key={i}>{val}</th>
                        }
                      })}

                      <th>Date Added</th>
                    </tr>
                  </thead>
                  <tbody className='text-gray-600'>
                    {loading.contactFetch ? (
                      <tr>
                        <td
                          colSpan={
                            6 +
                            groupField?.length +
                            (groupField?.length > 3 ? groupField?.length - 3 : 0)
                          }
                          className='text-center'
                        >
                          <Spinner animation='grow' />
                        </td>
                      </tr>
                    ) : (
                      <>
                        {data.contacts?.length > 0 ? (
                          data.contacts.map((val: ContactGroupMap, i) => (
                            <tr key={i}>
                              <td>
                                <div className='form-check form-check-sm form-check-custom form-check-solid'>
                                  <input
                                    className='form-check-input widget-9-check'
                                    type='checkbox'
                                    value={val?._id}
                                    checked={selectedContactGroupIDs.includes(val._id)}
                                    onChange={() => handleCheck(val)}
                                  />
                                </div>
                              </td>
                              <td className='cursor-pointer'>
                                <b>{val?.number}</b>
                              </td>
                              <td
                                data-bs-toggle='tooltip'
                                data-bs-placement='top'
                                title={val?.tags
                                  ?.filter((key: any) => key?.isHidden === false)
                                  ?.map((key: any) => key?.name)
                                  .join()}
                              >
                                <div className='d-flex gap-2 align-items-center flex-wrap'>
                                  {val?.tags
                                    ?.filter((data: any) => !data?.isHidden)
                                    ?.map(
                                      (
                                        data: {_id: string; name: string; color: string},
                                        i: number
                                      ) => (
                                        <div
                                          key={i}
                                          className='inbox-tags'
                                          style={{
                                            color: data?.color ? 'white' : 'black',
                                            backgroundColor: data?.color ? data?.color : '',
                                          }}
                                        >
                                          {data?.name}
                                        </div>
                                      )
                                    )}
                                </div>
                              </td>
                              {groupField?.includes('First Name') &&
                                (val?.fields?.findIndex(
                                  (val: any) => val?.name === 'First Name'
                                ) !== -1 ? (
                                  <td>
                                    {
                                      val?.fields?.[
                                        val?.fields?.findIndex(
                                          (val: any) => val?.name === 'First Name'
                                        )
                                      ]?.value
                                    }
                                  </td>
                                ) : (
                                  <td>-</td>
                                ))}
                              {groupField?.includes('Last Name') &&
                                (val?.fields?.findIndex((val: any) => val?.name === 'Last Name') !==
                                -1 ? (
                                  <td>
                                    {
                                      val?.fields?.[
                                        val?.fields?.findIndex(
                                          (val: any) => val?.name === 'Last Name'
                                        )
                                      ]?.value
                                    }
                                  </td>
                                ) : (
                                  <td>-</td>
                                ))}
                              {groupField?.includes('Full Name') &&
                                (val?.fields?.findIndex((val: any) => val?.name === 'Full Name') !==
                                -1 ? (
                                  <td>
                                    {
                                      val?.fields?.[
                                        val?.fields?.findIndex(
                                          (val: any) => val?.name === 'Full Name'
                                        )
                                      ]?.value
                                    }
                                  </td>
                                ) : (
                                  <td>-</td>
                                ))}

                              {groupField.map((Fields: any, i: number) => {
                                let check = val?.fields?.map((key: any) => key?.name)
                                if (!['First Name', 'Full Name', 'Last Name']?.includes(Fields)) {
                                  if (check?.includes(Fields)) {
                                    return (
                                      <td key={i}>
                                        {
                                          val?.fields?.find((key: any) => key?.name == Fields)
                                            ?.value
                                        }
                                      </td>
                                    )
                                  } else {
                                    return <td key={i}>--</td>
                                  }
                                }
                              })}

                              <td>{moment(val?.createdAt).format('DD/MM/YYYY')}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan={
                                6 +
                                groupField?.length +
                                (groupField?.length > 3 ? groupField?.length - 3 : 0)
                              }
                              className='text-center'
                            >
                              <b>Data not found</b>
                            </td>
                          </tr>
                        )}
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className='modal-footer w-100 mt-0 pt-0'>
        <CursorPaginate
          isFetching={
            loading.contactCountLoading ||
            loading.contactLoad ||
            loading.contactFetch ||
            loading.contactCountFetching
          }
          limit={data.params.limit ? data.params.limit : 10}
          handleChangSizeData={handleChangSizeData}
          next={data.next}
          prev={data.prev}
          loadedData={data.loadedContact}
          totalData={data.totalContacts}
          handleChange={handleChangePage}
        />
      </div>
    </Modal>
  )
}

export default QMContactListPopup
